import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useProfileRoutesConditions } from '../../../Profile/ProfileRoutes'
import { useSubscriptionRoutesConditions } from '../../../Subscriptions/SubscriptionsRoutes'
import { useToolsRoutesConditions } from '../../../Tools/ToolsRoutes'
import { useTraderRoomRoutesConditions } from '../../../Traders-Room/TradersRoomRoutes'
import { ChartPieIcon } from '../../../icons/ChartPieIcon'
import { CoinsIcon } from '../../../icons/CoinsIcon'
import { Graphcon } from '../../../icons/Graphcon'
import { TradingPlatformsIcon } from '../../../icons/TradingPlatformsIcon'
import { TickmillProductType } from '../../../model/TickmillProductType'
import { isDepositBeforeKYC } from '../../../utils/AccountAccess/accountKYCStatuses'
import { useAccountReadContext } from '../../../utils/AccountContextContext'
import { useMenu } from '../../context/MenuContext'
import { useProductReadContext } from '../../context/ProductContext'
import { MenuSection, filterMenuSections } from './useMenuSections'

export const useActionSections = (): {
  actionSections: MenuSection[]
} => {
  const { setActionItems } = useMenu()
  const { t } = useTranslation()
  const { product } = useProductReadContext()
  const prevRef = useRef<MenuSection[] | null>(null)

  const { account } = useAccountReadContext()

  const { isTradersRoomVisible } = useTraderRoomRoutesConditions()
  const { isFirstTimeDepositVisible } = useProfileRoutesConditions()
  const { isToolsVisible } = useToolsRoutesConditions()
  const { isSubscriptionsVisible } = useSubscriptionRoutesConditions()

  const showDepositButton = isTradersRoomVisible || isFirstTimeDepositVisible
  const showCreateTradingAccountButton = isTradersRoomVisible || isDepositBeforeKYC(account)
  const showPlatformButton = isToolsVisible || isDepositBeforeKYC(account)

  const actionSections: MenuSection[] = useMemo(
    () => [
      {
        id: 'deposit_action',
        label: t('Deposit'),
        icon: CoinsIcon,
        path: '/dashboard/traders-room/wallets/deposit',
        onClick: () => {},
        condition: showDepositButton,
      },
      {
        id: 'market_data_action',
        label: t('Market Data.title'),
        icon: ChartPieIcon,
        path: '/dashboard/subscriptions/market-data',
        onClick: () => {},
        products: [TickmillProductType.ETD],
        condition: isSubscriptionsVisible,
      },
      {
        id: 'create_ta_action',
        label: t('Create Trading Account'),
        icon: Graphcon,
        path: '/dashboard/traders-room/trading-accounts/new',
        onClick: () => {},
        products: [TickmillProductType.CFD],
        condition: showCreateTradingAccountButton,
      },
      {
        id: 'create_ta_cfd_action',
        label: t('Tabs.Trading Platforms'),
        icon: TradingPlatformsIcon,
        path: '/dashboard/tools/platforms/mt5',
        onClick: () => {},
        products: [TickmillProductType.CFD],
        condition: showPlatformButton,
      },
      {
        id: 'create_ta_etd_action',
        label: t('Tabs.Trading Platforms'),
        icon: TradingPlatformsIcon,
        path: '/dashboard/subscriptions/trading-platforms',
        onClick: () => {},
        products: [TickmillProductType.ETD],
        condition: showPlatformButton,
      },
    ],
    [
      t,
      showDepositButton,
      showCreateTradingAccountButton,
      showPlatformButton,
      isSubscriptionsVisible,
    ]
  )

  const filteredSections = useCallback(filterMenuSections, [])

  const filteredMenuSections = useMemo(
    () => filteredSections(actionSections, product),
    [filteredSections, actionSections, product]
  )

  useEffect(() => {
    const previous = prevRef.current
    const next = filteredMenuSections

    if (!previous || JSON.stringify(previous) !== JSON.stringify(next)) {
      setActionItems(next)
      prevRef.current = next
    }
  }, [filteredMenuSections, setActionItems])

  return { actionSections: filteredMenuSections }
}
